import { useQuery } from '@tanstack/react-query';
import { wishlistServices } from '@/src/app/[locale]/account/wishlist/services/wishlist';
import { primeServices } from '@/src/app/[locale]/account/prime/services/services';
import { profileServices } from '@/src/app/[locale]/account/profile/services/service';
import { CMSSectionsAPi, MinimalStoreInput } from './getCMS.service';
import { destinationApi } from './destinationService';

const useWishlist = () => {
  return useQuery({
    queryKey: ['wishlist'],
    queryFn: wishlistServices.getMyAllWishlist,
    refetchInterval: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: false,
  });
};
const usePrimeUser = () => {
  return useQuery({
    queryKey: ['prime'],
    queryFn: primeServices.getPrime,
    refetchInterval: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: false,
  });
};
const useDataUser = () => {
  return useQuery({
    queryKey: ['user'],
    queryFn: profileServices.getProfile,
    refetchInterval: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: false,
  });
};
const useAllProductsFromPageId = ({ pageId }: { pageId: string }) => {
  return useQuery({
    queryKey: ['all-products-cms', pageId],
    queryFn: () =>
      CMSSectionsAPi.getAllProductsFromCMS({
        pageId,
      }),
    refetchInterval: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: false,
    enabled: !!pageId,
  });
};

const useLiveStores = (stores: MinimalStoreInput[]) => {
  const storeKeys = stores?.map((s) => `${s.id}-${s.branchId}`).join(',') || '';

  return useQuery({
    queryKey: ['live-stores', storeKeys],
    queryFn: async () => {
      return await CMSSectionsAPi.getLiveStoresMinimal({ data: stores });
    },
    staleTime: 60000,
    refetchInterval: 60000,
    refetchOnMount: false,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: true,
    enabled: Boolean(stores && stores.length > 0),

    select: (responseData: any) => {
      if (!responseData || !Array.isArray(responseData)) return {};
      const timesMap: Record<number, string> = {};

      responseData.forEach((store: any) => {
        if (store && store.id) {
          timesMap[store.id] = store.estimatedDeliveryTime || '';
        }
      });
      return timesMap;
    },
  });
};

const useDestinationStore = (value: string | undefined, enabled = true) => {
  return useQuery({
    queryKey: ['destination', 'store', value],
    queryFn: () =>
      destinationApi.getDestinationStore({
        value: value || '',
      }),
    enabled: Boolean(enabled && value),
    staleTime: 1000 * 60 * 5,
  });
};

const useDestinationStores = (value: string[] | undefined, enabled = true) => {
  const key = value?.join(',') || '';
  return useQuery({
    queryKey: ['destination', 'stores', key],
    queryFn: () =>
      destinationApi.getDestinationStores({
        value: value || [],
      }),
    enabled: Boolean(enabled && value && value.length > 0),
    staleTime: 1000 * 60 * 5,
  });
};

const useDestinationProducts = (
  value: (string | number)[] | undefined,
  enabled = true,
  branchId?: number,
) => {
  const key = value?.join(',') || '';
  return useQuery({
    queryKey: ['destination', 'products', key, branchId],
    queryFn: () =>
      destinationApi.getDestinationProducts({
        value: value || [],
        branchId,
      }),
    enabled: Boolean(enabled && value && value.length > 0),
    staleTime: 1000 * 60 * 5,
  });
};

const useDestinationProduct = (value: string | undefined, enabled = true) => {
  return useQuery({
    queryKey: ['destination', 'product', value],
    queryFn: () =>
      destinationApi.getDestinationProduct({
        value: value || '',
      }),
    enabled: Boolean(enabled && value),
    staleTime: 1000 * 60 * 5,
  });
};

export {
  useWishlist,
  usePrimeUser,
  useDataUser,
  useAllProductsFromPageId,
  useLiveStores,
  useDestinationStore,
  useDestinationStores,
  useDestinationProducts,
  useDestinationProduct,
};
